import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Save, Upload, X, Star, Trash2, Plus, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/admin/PageHeader";
import { ResourceState } from "@/components/admin/ResourceState";
import { useVehicle } from "@/application/hooks/use-vehicle";
import { useVehicles } from "@/application/hooks/use-vehicles";
import {
  BODY_OPTIONS,
  FUEL_OPTIONS,
  TRANSMISSION_OPTIONS,
  uniqueSorted,
} from "@/domain/models/vehicle";
import { repositories } from "@/infrastructure/repositories";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";

export function VehicleEditorPage() {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const { status, data: existing, error, reload, isNew } = useVehicle(id);
  const { data: inventory } = useVehicles();
  const brands = useMemo(() => {
    const fromInventory = inventory ? uniqueSorted(inventory.map((v) => v.brand)) : [];
    return fromInventory.length > 0 ? fromInventory : ["Audi", "BMW", "Mercedes-AMG", "Polestar"];
  }, [inventory]);

  const [gallery, setGallery] = useState<string[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState("");
  const [dragging, setDragging] = useState(false);
  const [published, setPublished] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    if (existing) {
      setGallery([...existing.gallery]);
      setFeatures([...existing.features]);
      setFeatured(!!existing.tag);
      setPublished(true);
      setAvailable(existing.available);
    }
  }, [existing]);

  const save = async () => {
    try {
      if (isNew) {
        await repositories.vehicles.create({
          brand: brands[0] ?? "Audi",
          model: "New Listing",
          year: 2024,
          price: 0,
          mileage: 0,
          transmission: "Automatic",
          fuel: "Gasoline",
          body: "Sedan",
          condition: "New",
          available,
          power: 400,
          acceleration: 4,
          topSpeed: 250,
          drivetrain: "AWD",
          color: "Black",
          image: gallery[0] ?? "",
          gallery,
          features,
          description: "",
          tag: featured ? "Featured" : undefined,
        });
      } else if (id && existing) {
        await repositories.vehicles.update(id, {
          gallery,
          features,
          available,
          image: gallery[0] ?? existing.image,
          tag: featured ? existing.tag || "Featured" : undefined,
        });
      }
      toast.success(isNew ? t.admin.editor.created : t.admin.editor.updated, {
        description: t.admin.editor.savedDesc,
      });
      navigate("/admin/vehicles");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.admin.editor.saveFail);
    }
  };

  const removeImage = (i: number) => {
    setGallery((g) => g.filter((_, idx) => idx !== i));
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setFeatures((f) => [...f, newFeature.trim()]);
    setNewFeature("");
  };

  if (!isNew) {
    if (status === "loading" || status === "idle" || status === "error" || status === "empty") {
      return (
        <div>
          <div className="mb-4">
            <Link
              to="/admin/vehicles"
              className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> {t.admin.editor.back}
            </Link>
          </div>
          <ResourceState
            status={status === "empty" ? "empty" : status}
            error={error}
            onRetry={() => void reload()}
            emptyMessage={t.admin.editor.notFound}
          >
            {null}
          </ResourceState>
        </div>
      );
    }
  }

  return (
    <div>
      <div className="mb-4">
        <Link
          to="/admin/vehicles"
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> {t.admin.editor.back}
        </Link>
      </div>

      <PageHeader
        title={isNew ? t.admin.editor.newTitle : `${existing?.brand} ${existing?.model}`}
        description={
          isNew
            ? t.admin.editor.newDescription
            : t.admin.editor.editDescription
        }
        actions={
          <>
            <button className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border/60 bg-background/60 px-3 text-xs font-medium text-muted-foreground hover:text-foreground">
              <Sparkles className="h-3.5 w-3.5" /> {t.admin.editor.autofill}
            </button>
            <button
              onClick={() => void save()}
              className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition hover:-translate-y-0.5"
            >
              <Save className="h-3.5 w-3.5" /> {isNew ? t.admin.editor.createListing : t.admin.editor.saveChanges}
            </button>
          </>
        }
      />

      <div key={existing?.id ?? "new"} className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px]">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="mb-4 flex w-full flex-wrap justify-start gap-1 bg-surface/60 p-1">
            {(["general", "pricing", "specs", "features", "gallery", "seo", "status"] as const).map((section) => (
              <TabsTrigger key={section} value={section} className="text-xs">
                {t.admin.editor.sections[section]}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <Card>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t.admin.editor.brand}>
                  <Select defaultValue={existing?.brand ?? brands[0]}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t.admin.editor.model}><Input defaultValue={existing?.model} placeholder="e.g. RS7 Sportback" /></Field>
                <Field label={t.admin.editor.trim}><Input defaultValue="Performance" /></Field>
                <Field label={t.admin.editor.year}><Input type="number" defaultValue={existing?.year ?? 2024} /></Field>
                <Field label={t.admin.editor.vin}><Input defaultValue="WAUZZZ4G7EN012345" className="font-mono uppercase" /></Field>
                <Field label={t.admin.editor.stockNumber}><Input defaultValue={`MRD-${1200 + Math.floor(Math.random() * 100)}`} className="font-mono" /></Field>
                <Field label={t.admin.editor.slug} className="sm:col-span-2"><Input defaultValue={existing?.id} className="font-mono" /></Field>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t.admin.editor.exteriorColor}><Input defaultValue={existing?.color} /></Field>
                <Field label={t.admin.editor.interiorColor}><Input defaultValue="Valcona Black" /></Field>
              </div>
              <div className="mt-4">
                <Field label={t.admin.editor.description}>
                  <Textarea
                    rows={5}
                    defaultValue={existing?.description}
                    placeholder={t.admin.editor.descriptionPlaceholder}
                  />
                </Field>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="pricing" className="space-y-4">
            <Card>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t.admin.editor.listPrice}><Input type="number" defaultValue={existing?.price ?? 0} /></Field>
                <Field label={t.admin.editor.promoPrice}><Input type="number" placeholder={t.admin.editor.optional} /></Field>
                <Field label={t.admin.editor.downPayment}><Input type="number" defaultValue={20} /></Field>
                <Field label={t.admin.editor.apr}><Input type="number" step="0.1" defaultValue={4.9} /></Field>
                <Field label={t.admin.editor.term}><Input type="number" defaultValue={60} /></Field>
                <Field label={t.admin.editor.warranty}><Input type="number" defaultValue={4} /></Field>
              </div>
              <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
                <div className="text-xs uppercase tracking-wider text-primary">{t.admin.editor.estimatedMonthly}</div>
                <div className="mt-1 font-display text-2xl font-semibold tabular-nums">
                  ${Math.round(((existing?.price ?? 100000) * 0.8) / 60).toLocaleString()}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">{t.admin.editor.perMonth}</span>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="specs" className="space-y-4">
            <Card>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label={t.admin.editor.body}>
                  <Select defaultValue={existing?.body ?? BODY_OPTIONS[0]}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{BODY_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={t.admin.editor.fuel}>
                  <Select defaultValue={existing?.fuel ?? FUEL_OPTIONS[0]}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FUEL_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={t.admin.editor.transmission}>
                  <Select defaultValue={existing?.transmission ?? TRANSMISSION_OPTIONS[0]}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TRANSMISSION_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={t.admin.editor.drivetrain}><Input defaultValue={existing?.drivetrain ?? "AWD"} /></Field>
                <Field label={t.admin.editor.horsepower}><Input type="number" defaultValue={existing?.power ?? 400} /></Field>
                <Field label={t.admin.editor.torque}><Input type="number" defaultValue={480} /></Field>
                <Field label={t.admin.editor.acceleration}><Input type="number" step="0.1" defaultValue={existing?.acceleration ?? 4} /></Field>
                <Field label={t.admin.editor.topSpeed}><Input type="number" defaultValue={existing?.topSpeed ?? 250} /></Field>
                <Field label={t.admin.editor.mileage}><Input type="number" defaultValue={existing?.mileage ?? 0} /></Field>
                <Field label={t.admin.editor.doors}><Input type="number" defaultValue={4} /></Field>
                <Field label={t.admin.editor.seats}><Input type="number" defaultValue={5} /></Field>
                <Field label={t.admin.editor.condition}>
                  <Select defaultValue={existing?.condition ?? "New"}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">{t.admin.editor.conditionNew}</SelectItem>
                      <SelectItem value="Certified">{t.admin.editor.conditionCertified}</SelectItem>
                      <SelectItem value="Used">{t.admin.editor.conditionUsed}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="features" className="space-y-4">
            <Card>
              <div className="flex gap-2">
                <Input
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                  placeholder={t.admin.editor.addFeaturePlaceholder}
                />
                <button
                  onClick={addFeature}
                  className="inline-flex h-9 items-center gap-1 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground"
                >
                  <Plus className="h-3.5 w-3.5" /> {t.admin.editor.add}
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {features.map((f, i) => (
                  <span
                    key={i}
                    className="group inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 py-1 pl-3 pr-1 text-xs animate-fade-in"
                  >
                    {f}
                    <button
                      onClick={() => setFeatures((prev) => prev.filter((_, idx) => idx !== i))}
                      className="grid h-5 w-5 place-items-center rounded-full text-muted-foreground opacity-60 hover:bg-destructive/20 hover:text-destructive group-hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {features.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t.admin.editor.noFeatures}</p>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="gallery" className="space-y-4">
            <Card>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  toast.info(t.admin.editor.uploadInfo);
                }}
                className={cn(
                  "flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition",
                  dragging
                    ? "border-primary bg-primary/5"
                    : "border-border/60 hover:border-primary/40",
                )}
              >
                <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
                  <Upload className="h-5 w-5" />
                </div>
                <p className="mt-3 text-sm font-semibold">{t.admin.editor.dropImages}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t.admin.editor.dropHint}
                </p>
              </div>
              {gallery.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {gallery.map((src, i) => (
                    <div
                      key={i}
                      className="group relative aspect-[4/3] overflow-hidden rounded-lg ring-1 ring-border/60"
                    >
                      <img src={src} alt="" className="h-full w-full object-cover transition group-hover:scale-105" />
                      {i === 0 && (
                        <span className="absolute left-2 top-2 inline-flex items-center gap-0.5 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                          <Star className="h-2.5 w-2.5" /> {t.admin.editor.cover}
                        </span>
                      )}
                      <button
                        onClick={() => removeImage(i)}
                        className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-background/80 opacity-0 backdrop-blur transition hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="seo" className="space-y-4">
            <Card>
              <div className="space-y-4">
                <Field label={t.admin.editor.seoTitle}><Input defaultValue={`${existing?.brand ?? "Vehicle"} ${existing?.model ?? ""} ${t.admin.editor.forSale}`} /></Field>
                <Field label={t.admin.editor.metaDescription}>
                  <Textarea rows={3} defaultValue={existing?.description?.slice(0, 155)} />
                </Field>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t.admin.editor.canonical}><Input defaultValue={`https://meridian.example/${existing?.id ?? "listing"}`} className="font-mono text-xs" /></Field>
                  <Field label={t.admin.editor.keywords}><Input defaultValue="luxury, performance, certified" /></Field>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.admin.editor.searchPreview}</div>
                  <div className="mt-1 text-[13px] text-primary underline">
                    meridian.example/{existing?.id ?? "listing"}
                  </div>
                  <div className="text-sm font-medium">
                    {existing?.brand} {existing?.model} {t.admin.editor.forSale}
                  </div>
                  <div className="line-clamp-2 text-xs text-muted-foreground">
                    {existing?.description}
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="status" className="space-y-4">
            <Card>
              <ToggleRow
                title={t.admin.editor.published}
                description={t.admin.editor.publishedDesc}
                checked={published}
                onChange={setPublished}
              />
              <ToggleRow
                title={t.admin.editor.featuredHome}
                description={t.admin.editor.featuredHomeDesc}
                checked={featured}
                onChange={setFeatured}
              />
              <ToggleRow
                title={t.admin.editor.availablePurchase}
                description={t.admin.editor.availablePurchaseDesc}
                checked={available}
                onChange={setAvailable}
              />
              <ToggleRow
                title={t.admin.editor.allowFinancing}
                description={t.admin.editor.allowFinancingDesc}
                checked
                onChange={() => undefined}
              />
            </Card>
          </TabsContent>
        </Tabs>

        {/* Sidebar summary */}
        <aside className="space-y-4">
          <div className="sticky top-20 space-y-4">
            <Card>
              <h4 className="mb-3 font-display text-sm font-semibold">{t.admin.editor.publicationStatus}</h4>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t.admin.editor.status}</span>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 font-semibold",
                      published
                        ? "bg-emerald-500/15 text-emerald-400"
                        : "bg-amber-500/15 text-amber-400",
                    )}
                  >
                    {published ? t.admin.editor.live : t.admin.editor.draft}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t.admin.editor.featuredLabel}</span>
                  <span>{featured ? t.admin.common.yes : t.admin.common.no}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t.admin.editor.lastEdit}</span>
                  <span>{t.admin.common.justNow}</span>
                </div>
              </div>
              <button
                onClick={() => void save()}
                className="mt-4 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-primary text-xs font-semibold text-primary-foreground shadow-[var(--shadow-glow)]"
              >
                <Save className="h-3.5 w-3.5" /> {t.admin.editor.savePublish}
              </button>
            </Card>
            <Card>
              <h4 className="mb-2 font-display text-sm font-semibold">{t.admin.editor.completeness}</h4>
              <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full w-[86%] rounded-full bg-gradient-to-r from-primary to-primary/50" />
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t.admin.editor.completenessHint}
              </p>
            </Card>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-surface-elevated/60 p-5 shadow-sm backdrop-blur">
      {children}
    </div>
  );
}

function Field({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/40 py-3 last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
